const parseKmlCoordTriplet = function (coordText) {
  const parts = String(coordText || '').split(',').map(function (part) { return part.trim() })
  if (parts.length < 2) return null
  const lng = parseFloat(parts[0])
  const lat = parseFloat(parts[1])
  const alt = parts.length > 2 ? parseFloat(parts[2]) : null
  if (Number.isNaN(lat) || Number.isNaN(lng)) return null
  return {
    lat: lat,
    lng: lng,
    alt: Number.isNaN(alt) ? null : alt
  }
}

const kmlColorToLeaflet = function (kmlColor, fallbackColor, fallbackOpacity) {
  const fbColor = fallbackColor || '#3388ff'
  const fbOpacity = (fallbackOpacity === undefined) ? 1 : fallbackOpacity
  const raw = String(kmlColor || '').trim().replace('#', '')
  if (!raw) return { color: fbColor, opacity: fbOpacity }
  if (raw.length === 8) {
    const aa = parseInt(raw.slice(0, 2), 16)
    const bb = parseInt(raw.slice(2, 4), 16)
    const gg = parseInt(raw.slice(4, 6), 16)
    const rr = parseInt(raw.slice(6, 8), 16)
    if ([aa, bb, gg, rr].some(function (v) { return Number.isNaN(v) })) return { color: fbColor, opacity: fbOpacity }
    return {
      color: 'rgb(' + rr + ',' + gg + ',' + bb + ')',
      opacity: Math.max(0, Math.min(1, aa / 255))
    }
  }
  if (raw.length === 6) {
    return { color: '#' + raw, opacity: fbOpacity }
  }
  return { color: fbColor, opacity: fbOpacity }
}

const extractKmlStyle = function (styleEl) {
  const style = {
    color: '#3388ff',
    weight: 2,
    opacity: 1,
    fillColor: '#3388ff',
    fillOpacity: 0.2
  }
  if (!styleEl) return style
  const lineStyleEl = styleEl.getElementsByTagName('LineStyle')[0] || null
  const polyStyleEl = styleEl.getElementsByTagName('PolyStyle')[0] || null
  if (lineStyleEl) {
    const lineColorRaw = lineStyleEl.getElementsByTagName('color')[0]
    const lineWidthRaw = lineStyleEl.getElementsByTagName('width')[0]
    const lineColor = kmlColorToLeaflet(lineColorRaw ? lineColorRaw.textContent : null, style.color, style.opacity)
    style.color = lineColor.color
    style.opacity = lineColor.opacity
    const width = parseFloat(lineWidthRaw ? lineWidthRaw.textContent : '')
    if (!Number.isNaN(width) && width > 0) style.weight = width
  }
  if (polyStyleEl) {
    const polyColorRaw = polyStyleEl.getElementsByTagName('color')[0]
    const fillRaw = polyStyleEl.getElementsByTagName('fill')[0]
    const outlineRaw = polyStyleEl.getElementsByTagName('outline')[0]
    const polyColor = kmlColorToLeaflet(polyColorRaw ? polyColorRaw.textContent : null, style.fillColor, style.fillOpacity)
    style.fillColor = polyColor.color
    style.fillOpacity = polyColor.opacity
    if (fillRaw && String(fillRaw.textContent || '').trim() === '0') {
      style.fillOpacity = 0
    }
    if (outlineRaw && String(outlineRaw.textContent || '').trim() === '0') {
      style.opacity = 0
      style.weight = 0
    }
  }
  return style
}

const findKmlStyleById = function (xmlDoc, styleId) {
  if (!xmlDoc || !styleId) return null
  return Array.from(xmlDoc.getElementsByTagName('Style')).find(function (el) {
    return el.getAttribute('id') === styleId
  }) || null
}

const resolveStyleIdFromStyleMap = function (xmlDoc, styleMapId) {
  if (!xmlDoc || !styleMapId) return null
  const styleMapEl = Array.from(xmlDoc.getElementsByTagName('StyleMap')).find(function (el) {
    return el.getAttribute('id') === styleMapId
  }) || null
  if (!styleMapEl) return null
  const pairs = Array.from(styleMapEl.getElementsByTagName('Pair'))
  let targetStyleUrl = null
  const normalPair = pairs.find(function (pair) {
    const keyEl = pair.getElementsByTagName('key')[0]
    return keyEl && String(keyEl.textContent || '').trim() === 'normal'
  })
  const pairToUse = normalPair || pairs[0]
  if (pairToUse) {
    const styleUrlEl = pairToUse.getElementsByTagName('styleUrl')[0]
    targetStyleUrl = styleUrlEl ? String(styleUrlEl.textContent || '').trim() : null
  }
  if (!targetStyleUrl) return null
  const hashIndex = targetStyleUrl.lastIndexOf('#')
  return hashIndex >= 0 ? targetStyleUrl.slice(hashIndex + 1) : targetStyleUrl
}

const resolvePlacemarkStyle = function (pm, xmlDoc) {
  const defaultStyle = {
    color: '#3388ff',
    weight: 2,
    opacity: 1,
    fillColor: '#3388ff',
    fillOpacity: 0.2
  }
  if (!pm) return defaultStyle
  const inlineStyleEl = pm.getElementsByTagName('Style')[0] || null
  if (inlineStyleEl) {
    return Object.assign({}, defaultStyle, extractKmlStyle(inlineStyleEl))
  }
  const styleUrlEl = pm.getElementsByTagName('styleUrl')[0] || null
  const styleUrl = styleUrlEl ? String(styleUrlEl.textContent || '').trim() : ''
  if (!styleUrl) return defaultStyle
  const hashIndex = styleUrl.lastIndexOf('#')
  const styleRefId = hashIndex >= 0 ? styleUrl.slice(hashIndex + 1) : styleUrl
  const styleId = resolveStyleIdFromStyleMap(xmlDoc, styleRefId) || styleRefId
  const styleEl = findKmlStyleById(xmlDoc, styleId)
  if (!styleEl) return defaultStyle
  return Object.assign({}, defaultStyle, extractKmlStyle(styleEl))
}

const yieldKmlParseProgress = async function () {
  await new Promise(function (resolve) {
    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(function () {
        resolve()
      })
      return
    }
    setTimeout(resolve, 0)
  })
}

const parseKmlMission = function (xmlText, fnXmlToObj) {
  const parser = new DOMParser()
  const xml = parser.parseFromString(xmlText, 'application/xml')
  const serializer = new XMLSerializer()
  const documentEl = xml.getElementsByTagName('Document')[0] || xml.documentElement
  const folderEl = documentEl.getElementsByTagName('Folder')[0] || null
  const importPlacemarkEl = Array.from(documentEl.getElementsByTagName('Placemark')).find((pm) => {
    return pm.getElementsByTagName('LineString').length > 0
  }) || null
  const importCoordEl = importPlacemarkEl ? importPlacemarkEl.getElementsByTagName('coordinates')[0] : null
  const pointPlacemarks = folderEl ? Array.from(folderEl.getElementsByTagName('Placemark')) : []
  const allPlacemarks = Array.from(documentEl.getElementsByTagName('Placemark'))
  const waypoints = []
  const polygons = []

  pointPlacemarks.forEach((pm, index) => {
    const pointEl = pm.getElementsByTagName('Point')[0]
    if (!pointEl) return
    const coordEl = pointEl.getElementsByTagName('coordinates')[0]
    if (!coordEl) return
    const parsedCoord = parseKmlCoordTriplet(coordEl.textContent)
    if (!parsedCoord) return
    const props = (typeof fnXmlToObj === 'function') ? fnXmlToObj(pm) : {}
    props.__rawXml = serializer.serializeToString(pm)
    props.coordinates = { lat: parsedCoord.lat, lng: parsedCoord.lng }
    if (parsedCoord.alt !== null) {
      props.coordinates.alt = parsedCoord.alt
    }
    waypoints.push({
      idx: index,
      latlng: [parsedCoord.lat, parsedCoord.lng],
      altitude: parsedCoord.alt,
      props: props,
      xmlEl: pm,
      linkedXmlEls: []
    })
  })

  const lineCoords = []
  if (importCoordEl) {
    String(importCoordEl.textContent || '').trim().split(/\s+/).forEach((chunk) => {
      const parsedCoord = parseKmlCoordTriplet(chunk)
      if (parsedCoord) {
        lineCoords.push([parsedCoord.lat, parsedCoord.lng])
      }
    })
  }

  allPlacemarks.forEach((pm) => {
    const polygonEls = Array.from(pm.getElementsByTagName('Polygon') || [])
    if (!polygonEls.length) return
    const nameEl = pm.getElementsByTagName('name')[0]
    const label = nameEl ? String(nameEl.textContent || '').trim() : ''
    const style = resolvePlacemarkStyle(pm, xml)
    polygonEls.forEach((polygonEl) => {
      const outerEl = polygonEl.getElementsByTagName('outerBoundaryIs')[0] || polygonEl
      const coordEl = outerEl.getElementsByTagName('coordinates')[0]
      if (!coordEl) return
      const coords = []
      String(coordEl.textContent || '').trim().split(/\s+/).forEach((chunk) => {
        const parsedCoord = parseKmlCoordTriplet(chunk)
        if (parsedCoord) coords.push([parsedCoord.lat, parsedCoord.lng])
      })
      if (coords.length >= 3) {
        polygons.push({
          cord: coords,
          style: Object.assign({}, style),
          label: label
        })
      }
    })
  })

  return {
    xmlDoc: xml,
    documentEl: documentEl,
    folderEl: folderEl,
    importPlacemarkEl: importPlacemarkEl,
    importCoordEl: importCoordEl,
    waypoints: waypoints,
    lineCoords: lineCoords,
    polygons: polygons
  }
}

const parseKmlMissionWithProgress = async function (xmlText, fnXmlToObj, options) {
  const opts = (options && typeof options === 'object') ? options : {}
  const onProgress = (typeof opts.onProgress === 'function') ? opts.onProgress : function () {}
  const parser = new DOMParser()
  const xml = parser.parseFromString(xmlText, 'application/xml')
  const serializer = new XMLSerializer()
  const documentEl = xml.getElementsByTagName('Document')[0] || xml.documentElement
  const folderEl = documentEl.getElementsByTagName('Folder')[0] || null
  const importPlacemarkEl = Array.from(documentEl.getElementsByTagName('Placemark')).find((pm) => {
    return pm.getElementsByTagName('LineString').length > 0
  }) || null
  const importCoordEl = importPlacemarkEl ? importPlacemarkEl.getElementsByTagName('coordinates')[0] : null
  const pointPlacemarks = folderEl ? Array.from(folderEl.getElementsByTagName('Placemark')) : []
  const allPlacemarks = Array.from(documentEl.getElementsByTagName('Placemark'))
  const waypoints = []
  const polygons = []
  const totalPointPlacemarks = pointPlacemarks.length
  const waypointChunkSize = Math.max(10, Math.min(50, Math.ceil(totalPointPlacemarks / 40) || 10))

  onProgress({
    phase: 'parse-kml-waypoints',
    current: 0,
    total: totalPointPlacemarks,
    percent: totalPointPlacemarks > 0 ? 0 : 100
  })

  for (let index = 0; index < pointPlacemarks.length; index += 1) {
    const pm = pointPlacemarks[index]
    const pointEl = pm.getElementsByTagName('Point')[0]
    if (pointEl) {
      const coordEl = pointEl.getElementsByTagName('coordinates')[0]
      if (coordEl) {
        const parsedCoord = parseKmlCoordTriplet(coordEl.textContent)
        if (parsedCoord) {
          const props = (typeof fnXmlToObj === 'function') ? fnXmlToObj(pm) : {}
          props.__rawXml = serializer.serializeToString(pm)
          props.coordinates = { lat: parsedCoord.lat, lng: parsedCoord.lng }
          if (parsedCoord.alt !== null) {
            props.coordinates.alt = parsedCoord.alt
          }
          waypoints.push({
            idx: index,
            latlng: [parsedCoord.lat, parsedCoord.lng],
            altitude: parsedCoord.alt,
            props: props,
            xmlEl: pm,
            linkedXmlEls: []
          })
        }
      }
    }
    const processed = index + 1
    if ((processed % waypointChunkSize) === 0 || processed === totalPointPlacemarks) {
      onProgress({
        phase: 'parse-kml-waypoints',
        current: processed,
        total: totalPointPlacemarks,
        percent: totalPointPlacemarks > 0 ? Math.round((processed / totalPointPlacemarks) * 100) : 100
      })
      if (processed < totalPointPlacemarks) {
        await yieldKmlParseProgress()
      }
    }
  }

  const lineCoords = []
  if (importCoordEl) {
    String(importCoordEl.textContent || '').trim().split(/\s+/).forEach((chunk) => {
      const parsedCoord = parseKmlCoordTriplet(chunk)
      if (parsedCoord) {
        lineCoords.push([parsedCoord.lat, parsedCoord.lng])
      }
    })
  }

  allPlacemarks.forEach((pm) => {
    const polygonEls = Array.from(pm.getElementsByTagName('Polygon') || [])
    if (!polygonEls.length) return
    const nameEl = pm.getElementsByTagName('name')[0]
    const label = nameEl ? String(nameEl.textContent || '').trim() : ''
    const style = resolvePlacemarkStyle(pm, xml)
    polygonEls.forEach((polygonEl) => {
      const outerEl = polygonEl.getElementsByTagName('outerBoundaryIs')[0] || polygonEl
      const coordEl = outerEl.getElementsByTagName('coordinates')[0]
      if (!coordEl) return
      const coords = []
      String(coordEl.textContent || '').trim().split(/\s+/).forEach((chunk) => {
        const parsedCoord = parseKmlCoordTriplet(chunk)
        if (parsedCoord) coords.push([parsedCoord.lat, parsedCoord.lng])
      })
      if (coords.length >= 3) {
        polygons.push({
          cord: coords,
          style: Object.assign({}, style),
          label: label
        })
      }
    })
  })

  return {
    xmlDoc: xml,
    documentEl: documentEl,
    folderEl: folderEl,
    importPlacemarkEl: importPlacemarkEl,
    importCoordEl: importCoordEl,
    waypoints: waypoints,
    lineCoords: lineCoords,
    polygons: polygons
  }
}

export {
  parseKmlCoordTriplet,
  parseKmlMission,
  parseKmlMissionWithProgress,
  kmlColorToLeaflet,
  extractKmlStyle,
  findKmlStyleById,
  resolveStyleIdFromStyleMap,
  resolvePlacemarkStyle
}
